/**
 * Analytics Provider
 * Wrap your app with this to enable automatic analytics tracking
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth-context';
import { trackPageView } from '@/lib/tracking-helpers';
import { initializeUTMTracking } from '@/lib/utm-tracker';
import { useLocation } from 'wouter';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { user, isLoading } = useAuth();
  const [location] = useLocation();
  const hasTrackedInitialPage = useRef(false);

  // Initialize UTM tracking on every page load
  useEffect(() => {
    initializeUTMTracking(user?.id);
  }, [user?.id]);

  // Track page views automatically on route changes
  // Wait for auth to load before first tracking to ensure user_id is captured
  useEffect(() => {
    // Don't track until auth is loaded (to capture user_id)
    if (isLoading) return;

    // Always add a small delay to allow SEO component to set document.title
    // This ensures we capture the correct page title, not the default
    const delay = hasTrackedInitialPage.current ? 150 : 200;

    const timeoutId = setTimeout(() => {
      hasTrackedInitialPage.current = true;
      trackPageView(location, document.title);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [location, isLoading]);

  return <>{children}</>;
}
